import { useCallback, useRef, useState } from 'react'
import { useAuth, useSignIn, useUser, useSignUp } from '@clerk/react-router'
import { useNavigate } from 'react-router-dom'
import { getCounselorAuthMethods } from 'api/login'
import {
	clearPendingCounselorGrant,
	setCounselorLoginGrant,
	setPendingCounselorGrant,
} from 'shared/auth/counselorLoginGrant'
import {
	ACCESS_DENIED,
	CLERK_AUTH_FAILED,
	DIFFERENT_ACCOUNT_SIGNED_IN,
	GOOGLE_METHOD_UNAVAILABLE,
	INCORRECT_CREDENTIALS,
	INVALID_EMAIL,
	VERIFY_ACCESS_FAILED,
	evaluateCounselorAuthMethods,
	isValidCounselorEmail,
} from './counselorAuthGate'
import {
	decideExistingClerkSession,
	isAlreadySignedInError,
} from './existingClerkSession'

export { isValidCounselorEmail }

const OIDC_ID = ''
const SAML_ID = ''

const bindGrantFromClerk = (email, sessionId) => {
	const session =
		typeof window !== 'undefined' ? window.Clerk?.session : null
	const user = typeof window !== 'undefined' ? window.Clerk?.user : null
	const sid = String(sessionId || session?.id || '').trim()
	const uid = String(
		session?.userId || session?.user?.id || user?.id || '',
	).trim()
	if (!sid || !uid) return
	setCounselorLoginGrant({
		sessionId: sid,
		userId: uid,
		email,
	})
}

export const useLoginLogic = () => {
	const { isLoaded: isSignInReady, signIn, setActive } = useSignIn()
	const { isLoaded: isSignUpReady, signUp } = useSignUp()
	const { isSignedIn, user } = useUser()
	const { sessionId, userId } = useAuth()
	const ssO = OIDC_ID || SAML_ID

	const navigate = useNavigate()
	const requestSeq = useRef(0)

	const currentUser = isSignedIn
		? {
				id: user.id,
				displayName:
					user.fullName ||
					user.firstName ||
					user.username ||
					user.primaryEmailAddress?.emailAddress ||
					'User',
				email: user.primaryEmailAddress?.emailAddress || '',
				imageUrl: user.imageUrl || '',
		  }
		: null

	const [phone, setPhone] = useState('')
	const [smsCode, setSmsCode] = useState('')
	const [phoneStep, setPhoneStep] = useState('enter')
	const [activeTab, setActiveTab] = useState('email')
	const [loading, setLoading] = useState(false)
	const [authAction, setAuthAction] = useState(null)
	const [error, setError] = useState('')
	const [email, setEmail] = useState('')
	const [emailCode, setEmailCode] = useState('')
	const [emailStep, setEmailStep] = useState('form')
	const [password, setPassword] = useState('')
	const [isVisiblePassword, setIsVisiblePassword] = useState(false)
	const [phoneFlow, setPhoneFlow] = useState(null)
	const [resetCode, setResetCode] = useState('')
	// eslint-disable-next-line no-unused-vars
	const [resetNewPassword, setResetNewPassword] = useState('')

	const emailValid = isValidCounselorEmail(email)
	const isAuthBusy = Boolean(authAction) || loading

	const googleExplanation = !String(email || '').trim()
		? 'Enter your email to continue with Google.'
		: !emailValid
			? INVALID_EMAIL
			: error === ACCESS_DENIED ||
				  error === GOOGLE_METHOD_UNAVAILABLE ||
				  error === VERIFY_ACCESS_FAILED
				? error
				: ''

	const verifyCounselorMethod = useCallback(async (rawEmail, method) => {
		const normalized = String(rawEmail || '').trim().toLowerCase()
		if (!isValidCounselorEmail(normalized)) {
			setError(INVALID_EMAIL)
			return null
		}

		const seq = ++requestSeq.current
		setError('')

		try {
			const response = await getCounselorAuthMethods(normalized)
			if (seq !== requestSeq.current) return null

			const result = evaluateCounselorAuthMethods(
				normalized,
				method,
				response?.data || {},
			)

			if (!result.ok) {
				setError(result.error)
				return null
			}

			return { email: result.email, methods: result.methods }
		} catch (err) {
			if (seq !== requestSeq.current) return null
			console.error(
				'[Counselor Auth] counselor-auth-methods failed:',
				err,
			)
			setError(VERIFY_ACCESS_FAILED)
			return null
		}
	}, [])

	const settleExistingSession = expectedEmail => {
		const clerkUser =
			user || (typeof window !== 'undefined' ? window.Clerk?.user : null)
		const decision = decideExistingClerkSession({
			user: clerkUser,
			sessionId:
				sessionId ||
				(typeof window !== 'undefined' ? window.Clerk?.session?.id : ''),
			userId:
				userId ||
				clerkUser?.id ||
				(typeof window !== 'undefined' ? window.Clerk?.user?.id : ''),
			expectedEmail,
		})

		if (decision.action === 'enter') {
			setCounselorLoginGrant({
				sessionId: decision.sessionId,
				userId: decision.userId,
				email: decision.email,
			})
			clearPendingCounselorGrant()
			navigate('/', { replace: true })
			return 'entered'
		}

		if (decision.action === 'mismatch') {
			setError(DIFFERENT_ACCOUNT_SIGNED_IN)
			return 'mismatch'
		}

		return decision.action
	}

	const handleEmailPasswordAuth = async e => {
		e?.preventDefault()
		if (!isSignInReady || isAuthBusy) return

		setAuthAction('email')
		setError('')

		const access = await verifyCounselorMethod(email, 'email')
		if (!access) {
			setAuthAction(null)
			return
		}

		const existing = settleExistingSession(access.email)
		if (existing === 'entered' || existing === 'mismatch') {
			setAuthAction(null)
			return
		}

		try {
			const r = await signIn.create({
				identifier: access.email,
				password,
			})
			if (r.status === 'complete') {
				await setActive({ session: r.createdSessionId })
				bindGrantFromClerk(access.email, r.createdSessionId)
				navigate('/', { replace: true })
				return
			}
			setError(CLERK_AUTH_FAILED)
		} catch (err) {
			console.error('[Counselor Auth] Email/password failed:', err)
			if (isAlreadySignedInError(err)) {
				const settled = settleExistingSession(access.email)
				if (settled === 'entered' || settled === 'mismatch') return
			}
			const clerkErr = err?.errors?.[0]
			const code = clerkErr?.code || ''

			switch (code) {
				case 'form_password_incorrect':
				case 'form_identifier_not_found': {
					setError(INCORRECT_CREDENTIALS)
					return
				}
				case 'strategy_for_user_not_allowed':
				case 'not_allowed_to_sign_in':
				case 'identifier_exists_but_no_password': {
					try {
						await signIn.create({ identifier: access.email })
						await signIn.prepareFirstFactor({ strategy: 'email_code' })
						setEmailStep('verifyCode')
						setError('')
					} catch (e2) {
						console.error(
							'[Counselor Auth] Email code fallback failed:',
							e2,
						)
						setError(CLERK_AUTH_FAILED)
					}
					return
				}
				default: {
					setError(CLERK_AUTH_FAILED)
					return
				}
			}
		} finally {
			setAuthAction(null)
		}
	}

	const handleEmailCodeVerify = async e => {
		e && e.preventDefault()
		if (isAuthBusy) return
		setLoading(true)
		setError('')

		try {
			const r = await signUp.attemptEmailAddressVerification({
				code: emailCode,
			})

			if (r.status === 'complete') {
				if (r.createdSessionId) {
					await setActive({ session: r.createdSessionId })
					bindGrantFromClerk(email, r.createdSessionId)
					navigate('/', { replace: true })
					return
				}
				if (password) {
					const si = await signIn.create({ identifier: email, password })
					if (si.status === 'complete') {
						await setActive({ session: si.createdSessionId })
						bindGrantFromClerk(email, si.createdSessionId)
						navigate('/', { replace: true })
						return
					}
				}
				await signIn.create({ identifier: email })
				await signIn.prepareFirstFactor({ strategy: 'email_code' })
				setEmailStep('verifyCode')
				setError(
					'Email verified. We sent you a sign-in code – please enter it.',
				)
				return
			}

			if (r.status === 'missing_requirements') {
				setError(CLERK_AUTH_FAILED)
				return
			}

			const done = await signUp.reload()
			if (done?.status === 'complete') {
				if (done.createdSessionId) {
					await setActive({ session: done.createdSessionId })
					bindGrantFromClerk(email, done.createdSessionId)
				} else if (password) {
					const si = await signIn.create({ identifier: email, password })
					if (si.status === 'complete') {
						await setActive({ session: si.createdSessionId })
						bindGrantFromClerk(email, si.createdSessionId)
					}
				}
				navigate('/', { replace: true })
				return
			}

			setError(CLERK_AUTH_FAILED)
		} catch (err) {
			console.error('[Counselor Auth] Email code verify failed:', err)
			const msg =
				err?.errors?.[0]?.longMessage ||
				err?.errors?.[0]?.message ||
				err.message ||
				''

			if (/already been verified/i.test(msg)) {
				try {
					if (password) {
						const si = await signIn.create({ identifier: email, password })
						if (si.status === 'complete') {
							await setActive({ session: si.createdSessionId })
							bindGrantFromClerk(email, si.createdSessionId)
							navigate('/', { replace: true })
							return
						}
					} else {
						await signIn.create({ identifier: email })
						await signIn.prepareFirstFactor({ strategy: 'email_code' })
						setEmailStep('verifyCode')
						setError(
							'Email already verified. Enter the sign-in code we sent.',
						)
						return
					}
				} catch (e2) {
					console.error(
						'[Counselor Auth] Email code verify fallback failed:',
						e2,
					)
				}
			}

			setError(CLERK_AUTH_FAILED)
		} finally {
			setLoading(false)
		}
	}

	const resetEmailFlow = () => {
		if (isAuthBusy) return
		setError('')
		setEmailStep('form')
		setEmail('')
		setEmailCode('')
	}

	const handelForgotPassword = () => {
		if (isAuthBusy) return
		setEmailStep('resetPassword')
	}

	const handleResetRequest = async e => {
		e?.preventDefault()
		if (!isSignInReady || isAuthBusy) return
		setLoading(true)
		setError('')
		try {
			await signIn.create({ identifier: email })

			await signIn.prepareFirstFactor({
				strategy: 'reset_password_email_code',
			})

			setEmailStep('verifyCodeReset')
			setError('If such an email exists, we have sent you a recovery code.')
		} catch (err) {
			console.error('[Counselor Auth] Password reset request failed:', err)
			setEmailStep('verifyCodeReset')
			setError('If such an email exists, we have sent you a recovery code.')
		} finally {
			setLoading(false)
		}
	}

	const handleResetVerifyCode = async e => {
		e?.preventDefault()
		if (!isSignInReady || isAuthBusy) return
		setLoading(true)
		setError('')
		try {
			const res = await signIn.attemptFirstFactor({
				strategy: 'reset_password_email_code',
				code: resetCode,
			})

			if (res.status === 'needs_new_password') {
				setEmailStep('setNewPassword')
			} else if (res.status === 'complete' && res.createdSessionId) {
				await setActive({ session: res.createdSessionId })
				bindGrantFromClerk(email, res.createdSessionId)
				navigate('/', { replace: true })
			} else {
				setError('Please enter a new password.')
				setEmailStep('setNewPassword')
			}
		} catch (err) {
			console.error('[Counselor Auth] Password reset verify failed:', err)
			setError(CLERK_AUTH_FAILED)
		} finally {
			setLoading(false)
		}
	}

	const resetPasswordFlow = () => {
		if (isAuthBusy) return
		setEmailStep('form')
		setResetCode('')
		setResetNewPassword('')
		setError('')
	}

	const handleGoogleLogin = async () => {
		if (!isSignInReady || isAuthBusy) return

		setAuthAction('google')
		setError('')

		const access = await verifyCounselorMethod(email, 'google')
		if (!access) {
			setAuthAction(null)
			return
		}

		const existing = settleExistingSession(access.email)
		if (existing === 'entered' || existing === 'mismatch') {
			setAuthAction(null)
			return
		}

		setPendingCounselorGrant({ email: access.email, method: 'google' })
		try {
			await signIn.authenticateWithRedirect({
				strategy: 'oauth_google',
				redirectUrl: '/sso-callback',
				redirectUrlComplete: '/',
			})
		} catch (err) {
			console.error('[Counselor Auth] Google OAuth failed:', err)
			if (isAlreadySignedInError(err)) {
				const settled = settleExistingSession(access.email)
				if (settled === 'entered' || settled === 'mismatch') {
					setAuthAction(null)
					return
				}
			}
			clearPendingCounselorGrant()
			setError(CLERK_AUTH_FAILED)
			setAuthAction(null)
		}
	}

	const handleSSOLogin = () => {
		if (!isSignInReady) return
		return signIn.authenticateWithRedirect({
			strategy: ssO?.startsWith('saml') ? 'saml' : 'oidc',
			identifier: ssO,
			redirectUrl: '/sso-callback',
			redirectUrlComplete: '/',
		})
	}

	const handleMicrosoft = async userEmail => {
		if (!isSignInReady || isAuthBusy) return

		setAuthAction('sso')
		setError('')

		const access = await verifyCounselorMethod(userEmail || email, 'sso')
		if (!access) {
			setAuthAction(null)
			return
		}

		const existing = settleExistingSession(access.email)
		if (existing === 'entered' || existing === 'mismatch') {
			setAuthAction(null)
			return
		}

		setPendingCounselorGrant({ email: access.email, method: 'sso' })
		try {
			await signIn.authenticateWithRedirect({
				strategy: 'enterprise_sso',
				identifier: access.email,
				redirectUrl: '/sso-callback',
				redirectUrlComplete: '/',
			})
		} catch (err) {
			console.error('[Counselor Auth] SSO failed:', err)
			if (isAlreadySignedInError(err)) {
				const settled = settleExistingSession(access.email)
				if (settled === 'entered' || settled === 'mismatch') {
					setAuthAction(null)
					return
				}
			}
			clearPendingCounselorGrant()
			setError(CLERK_AUTH_FAILED)
			setAuthAction(null)
		}
	}

	const normalizePhone = v => {
		let x = (v || '').replace(/[^\d+]/g, '')
		if (!x.startsWith('+')) x = '+' + x
		return x
	}
	const isE164 = v => /^\+\d{10,15}$/.test(v)

	const handlePhoneSendCode = async e => {
		e && e.preventDefault()
		const identifier = normalizePhone(phone)

		if (!isE164(identifier)) {
			setError('Enter the number with country code')
			return
		}
		if (!isSignInReady || !isSignUpReady || isAuthBusy) return

		setLoading(true)
		setError('')
		try {
			await signIn.create({ identifier: identifier })

			const factors = (signIn.supportedFirstFactors || []).filter(
				f => f.strategy === 'phone_code',
			)

			const factor =
				factors.find(
					f => (f.phoneNumber || '').replace(/\s/g, '') === identifier,
				) || factors[0]

			if (!factor || !factor.phoneNumberId) {
				throw new Error('Phone sign-in is unavailable for this user')
			}

			await signIn.prepareFirstFactor({
				strategy: 'phone_code',
				phoneNumberId: factor.phoneNumberId,
			})
			setPhoneFlow('signin')
			setPhoneStep('code')
		} catch (err1) {
			try {
				await signUp.create({ phoneNumber: identifier })
				await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' })
				setPhoneFlow('signup')
				setPhoneStep('code')
			} catch (err2) {
				console.error('[Counselor Auth] Phone send code failed:', err2)
				setError(CLERK_AUTH_FAILED)
			}
		} finally {
			setLoading(false)
		}
	}

	const handlePhoneVerifyCode = async e => {
		e && e.preventDefault()
		if (isAuthBusy) return
		setLoading(true)
		setError('')
		try {
			if (phoneFlow === 'signin') {
				const res = await signIn.attemptFirstFactor({
					strategy: 'phone_code',
					code: smsCode,
				})
				if (res.status === 'complete') {
					await setActive({ session: res.createdSessionId })
					navigate('/', { replace: true })
				}
			} else if (phoneFlow === 'signup') {
				const res = await signUp.attemptPhoneNumberVerification({
					code: smsCode,
				})
				if (res.status === 'complete') {
					await setActive({ session: res.createdSessionId })
					navigate('/', { replace: true })
				} else if (res.status === 'missing_requirements') {
					setError(CLERK_AUTH_FAILED)
				}
			} else {
				setError('First send the code to your phone')
			}
		} catch (err) {
			console.error('[Counselor Auth] Phone verify failed:', err)
			const msg =
				(err?.errors && err.errors[0]?.longMessage) ||
				(err?.errors && err.errors[0]?.message) ||
				err.message ||
				''
			if (/already been verified/i.test(msg)) {
				return navigate('/', { replace: true })
			}
			setError(CLERK_AUTH_FAILED)
		} finally {
			setLoading(false)
		}
	}

	const resetPhone = () => {
		if (isAuthBusy) return
		setPhoneStep('enter')
		setPhone('')
		setSmsCode('')
		setPhoneFlow(null)
		setError('')
	}

	const onChangeEmail = value => {
		setEmail(value)
		if (error) {
			setError('')
		}
	}

	const onSetActiveTab = tab => {
		if (isAuthBusy) return
		setActiveTab(tab)
	}

	return {
		currentUser,
		activeTab,
		setActiveTab: onSetActiveTab,
		loading: isAuthBusy,
		authAction,
		error,
		handleGoogleLogin,
		handleSSOLogin,
		emailStep,
		emailCode,
		setEmailCode,
		handleEmailPasswordAuth,
		handleEmailCodeVerify,
		handleMicrosoft,
		setEmail: onChangeEmail,
		email,
		emailValid,
		googleExplanation,
		password,
		setPassword,
		isVisiblePassword,
		setIsVisiblePassword,
		resetEmailFlow,
		phone,
		setPhone,
		smsCode,
		setSmsCode,
		phoneStep,
		setPhoneStep,
		handlePhoneSendCode,
		handlePhoneVerifyCode,
		resetPhone,
		handelForgotPassword,
		handleResetRequest,
		handleResetVerifyCode,
		resetPasswordFlow,
		setResetCode,
		resetCode,
	}
}
